/**
 * RASHII USER OBLIGATIONS PANEL
 * ==============================
 * Phase 2 Soft Visibility - User-facing deadline view
 * 
 * Design Principles:
 * - No red. No penalty language. Just clarity.
 * - Soft visibility increases discipline before enforcement.
 * - Different framing per role:
 *   - Buyers: "Your Upcoming Obligations"
 *   - Sellers: "Transaction Milestones"
 *   - Agents: "Deal Coordination Timeline"
 */

import React, { useState, useEffect } from 'react';
import {
  Clock, CheckCircle2, Calendar, ChevronRight, ArrowRight,
  Star, AlertCircle, Bell, FileText, Timer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import api from '@/services/api';

// ============ CONSTANTS ============

const ROLE_CONFIG = {
  buyer: {
    title: "Your Upcoming Obligations",
    subtitle: "Stay on track with your transaction milestones",
    emptyMessage: "No active obligations. Start browsing properties!",
    needsAttentionLabel: "Needs Your Attention",
    comingSoonLabel: "Coming Up Soon",
    completedLabel: "Completed"
  },
  seller: {
    title: "Transaction Milestones",
    subtitle: "Track your property sale progress",
    emptyMessage: "No active transactions. List your property to get started!",
    needsAttentionLabel: "Action Required",
    comingSoonLabel: "Upcoming",
    completedLabel: "Done"
  },
  agent: {
    title: "Deal Coordination Timeline",
    subtitle: "Monitor all deal milestones",
    emptyMessage: "No active deals assigned to you.",
    needsAttentionLabel: "Requires Attention",
    comingSoonLabel: "Coming Soon",
    completedLabel: "Completed"
  }
};

const STATUS_STYLES = {
  "Needs Attention": {
    bg: "bg-amber-600/10",
    border: "border-amber-600/30",
    text: "text-amber-400",
    icon: AlertCircle,
    priority: 1
  },
  "Coming Soon": {
    bg: "bg-blue-600/10",
    border: "border-blue-600/30",
    text: "text-blue-400",
    icon: Timer,
    priority: 2
  },
  "This Week": {
    bg: "bg-cyan-600/10",
    border: "border-cyan-600/30",
    text: "text-cyan-400",
    icon: Calendar,
    priority: 3
  },
  "Upcoming": {
    bg: "bg-secondary/30",
    border: "border-white/10",
    text: "text-muted-foreground",
    icon: Clock,
    priority: 4
  },
  "Completed": {
    bg: "bg-green-600/10",
    border: "border-green-600/30",
    text: "text-green-400",
    icon: CheckCircle2,
    priority: 5
  }
};

// ============ HELPER FUNCTIONS ============

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AE', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

const getDaysLabel = (days) => {
  if (days === null || days === undefined) return '';
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 7) return `In ${days} days`;
  return `In ${Math.ceil(days / 7)} weeks`;
};

// ============ SINGLE OBLIGATION CARD ============

const ObligationCard = ({ obligation, compact = false }) => {
  const statusStyle = STATUS_STYLES[obligation.status] || STATUS_STYLES["Upcoming"];
  const StatusIcon = statusStyle.icon;

  if (compact) {
    return (
      <div 
        className={`flex items-center justify-between p-3 rounded-lg ${statusStyle.bg} border ${statusStyle.border}`}
        data-testid={`obligation-${obligation.deadline_id}`}
      >
        <div className="flex items-center gap-3">
          <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
          <div>
            <p className="text-sm font-medium">{obligation.label}</p>
            <p className="text-xs text-muted-foreground">{formatDate(obligation.date)}</p>
          </div>
        </div>
        {obligation.days_remaining !== null && (
          <Badge variant="outline" className={`${statusStyle.text} border-current`}>
            {getDaysLabel(obligation.days_remaining)}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <Card 
      className={`${statusStyle.bg} border ${statusStyle.border}`}
      data-testid={`obligation-${obligation.deadline_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${statusStyle.bg}`}>
              <StatusIcon className={`w-5 h-5 ${statusStyle.text}`} />
            </div>
            <div>
              <h4 className="font-medium text-sm">{obligation.label}</h4>
              <p className="text-xs text-muted-foreground mt-0.5">{obligation.description}</p>
              
              {/* Date info */}
              <div className="flex items-center gap-2 mt-2">
                <Calendar className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs">{formatDate(obligation.date)}</span>
                {obligation.days_remaining !== null && (
                  <Badge variant="outline" className={`text-xs ${statusStyle.text} border-current`}>
                    {getDaysLabel(obligation.days_remaining)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {obligation.is_completed && (
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============ OBLIGATIONS SECTION ============

const ObligationsSection = ({ title, obligations, variant = "default" }) => {
  if (!obligations || obligations.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        {title}
        <Badge variant="secondary" className="text-xs">{obligations.length}</Badge>
      </h4>
      <div className="space-y-2">
        {obligations.map((obligation) => (
          <ObligationCard 
            key={obligation.deadline_id} 
            obligation={obligation}
            compact={variant === "compact"}
          />
        ))}
      </div>
    </div>
  );
};

// ============ MAIN USER OBLIGATIONS PANEL ============

const UserObligationsPanel = ({ role = "buyer", maxItems = 5, showAll = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = ROLE_CONFIG[role] || ROLE_CONFIG.buyer;

  useEffect(() => {
    fetchObligations();
  }, [role]);

  const fetchObligations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/contracts/my-deadlines');
      setData(response.data);
    } catch (err) {
      console.error('Error fetching obligations:', err);
      setError('Unable to load obligations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="py-8 text-center">
          <Clock className="w-6 h-6 mx-auto animate-pulse text-primary" />
          <p className="text-sm text-muted-foreground mt-2">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-6 h-6 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.total === 0) {
    return (
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            {config.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <CheckCircle2 className="w-8 h-8 mx-auto text-green-400 mb-2" />
          <p className="text-sm text-muted-foreground">{config.emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const { categories, summary } = data;

  return (
    <Card className="glass-card border-white/10" data-testid="user-obligations-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              {config.title}
            </CardTitle>
            <CardDescription className="mt-1">{config.subtitle}</CardDescription>
          </div>
          {data.total > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold">{summary.completed}/{data.total}</p>
              <p className="text-xs text-muted-foreground">completed</p>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {data.total > 0 && (
          <div className="mt-4">
            <Progress 
              value={(summary.completed / data.total) * 100} 
              className="h-2"
            />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Needs Attention - Priority */}
        {categories.needs_attention?.length > 0 && (
          <ObligationsSection 
            title={config.needsAttentionLabel}
            obligations={showAll ? categories.needs_attention : categories.needs_attention.slice(0, maxItems)}
            variant="default"
          />
        )}

        {/* Coming Soon */}
        {categories.coming_soon?.length > 0 && (
          <ObligationsSection 
            title={config.comingSoonLabel}
            obligations={showAll ? categories.coming_soon : categories.coming_soon.slice(0, maxItems)}
            variant="compact"
          />
        )}

        {/* Upcoming - less prominent */}
        {showAll && categories.upcoming?.length > 0 && (
          <ObligationsSection 
            title="Later"
            obligations={categories.upcoming}
            variant="compact"
          />
        )}

        {/* Completed - collapsed by default */}
        {showAll && categories.completed?.length > 0 && (
          <>
            <Separator className="my-4" />
            <ObligationsSection 
              title={config.completedLabel}
              obligations={categories.completed}
              variant="compact"
            />
          </>
        )}

        {/* View All link */}
        {!showAll && data.total > maxItems && (
          <Button variant="ghost" size="sm" className="w-full text-xs">
            View all {data.total} milestones
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ============ DEAL MILESTONE TIMELINE ============

const DealMilestoneTimeline = ({ dealId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (dealId) {
      fetchMilestones();
    }
  }, [dealId]);

  const fetchMilestones = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/contracts/deal/${dealId}/user-milestones`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching milestones:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Clock className="w-5 h-5 animate-pulse text-primary" />
      </div>
    );
  }

  if (!data || data.milestones.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{data?.message || "No milestones yet"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="deal-milestone-timeline">
      {/* Progress Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Transaction Progress</p>
          <p className="text-xs text-muted-foreground">
            {data.completed} of {data.total} milestones complete
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">{data.progress}%</p>
        </div>
      </div>
      <Progress value={data.progress} className="h-2" />

      {/* Next Milestone Highlight */}
      {data.next_milestone && (
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-primary mb-1">
              <ArrowRight className="w-3 h-3" />
              NEXT UP
            </div>
            <p className="font-medium">{data.next_milestone.label}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(data.next_milestone.date)} • {data.next_milestone.responsibility_label}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div className="relative pl-6 space-y-4">
        {/* Timeline line */}
        <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-white/10" />

        {data.milestones.map((milestone, idx) => {
          const isCompleted = milestone.is_completed;
          const isNext = !isCompleted && (idx === 0 || data.milestones[idx - 1]?.is_completed);
          
          return (
            <div key={milestone.id} className="relative">
              {/* Timeline dot */}
              <div 
                className={`absolute -left-4 w-4 h-4 rounded-full border-2 ${
                  isCompleted 
                    ? 'bg-green-600 border-green-600' 
                    : isNext
                    ? 'bg-primary border-primary animate-pulse'
                    : 'bg-background border-white/30'
                }`}
              >
                {isCompleted && (
                  <CheckCircle2 className="w-3 h-3 text-white absolute top-0.5 left-0.5" />
                )}
              </div>

              {/* Content */}
              <div className={`pb-4 ${isCompleted ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-sm font-medium ${isCompleted ? 'line-through' : ''}`}>
                      {milestone.label}
                    </p>
                    <p className="text-xs text-muted-foreground">{milestone.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs">{formatDate(milestone.date)}</p>
                    {milestone.days_remaining !== null && !isCompleted && (
                      <p className={`text-xs ${
                        milestone.days_remaining < 0 ? 'text-amber-400' : 'text-muted-foreground'
                      }`}>
                        {getDaysLabel(milestone.days_remaining)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Responsibility indicator */}
                {milestone.is_my_responsibility && !isCompleted && (
                  <Badge className="mt-2 text-xs bg-primary/20 text-primary">
                    {milestone.responsibility_label}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserObligationsPanel;
export { ObligationCard, DealMilestoneTimeline };
